// lib
import { forwardRef, useState, useRef, useMemo, useCallback, useEffect, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'

// components
import AdvancedFilter from '@aytm/app/stats/components/advanced-filter/AdvancedFilter'

const AdvancedFilterContainer = forwardRef(function AdvancedFilterContainer(
  {
    dataTestID,
    className,
    pattern,
    parser,
    surveySchema,
    clusters,
    parserResultEof,
    prefix,
    hintPosition,
    options = {},
    disabled,
    onChange,
    onBlur: onBlurProp
  },
  ref
) {
  const [advancedFilterPattern, setAdvancedFilterPattern] = useState(pattern)

  const advancedFilterRef = useRef(null)

  const timerRef = useRef(null)
  // What the field holds right now. The editor flushes its own pending text into
  // onPatternChange and blurs inside one handler, so onBlur runs a render too early
  // to read that text off state.
  const valueRef = useRef(pattern)

  const parserResultPattern = useMemo(
    () =>
      parser.analyzeString({
        filter: advancedFilterPattern.trim(),
        clusters: clusters,
        saveTokens: true,
        ...options
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [advancedFilterPattern, surveySchema, clusters]
  )

  const onPatternChange = useCallback(
    value => {
      valueRef.current = value
      setAdvancedFilterPattern(value)

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        'function' == typeof onChange && onChange(value)
      }, 1650)
    },
    [onChange]
  )

  const onBlur = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null

    if (pattern != valueRef.current) {
      'function' == typeof onChange && onChange(valueRef.current)
    }

    // Carries the text the field holds, not the editor's (event, editorState): the flush above only
    // reaches state on the next render, so a caller reading its own state here would see it empty.
    'function' == typeof onBlurProp && onBlurProp(valueRef.current)
  }, [pattern, onChange, onBlurProp])

  useEffect(() => {
    valueRef.current = pattern
    setAdvancedFilterPattern(pattern)
  }, [pattern])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  useImperativeHandle(ref, () => ({ focus: () => advancedFilterRef.current?.focus() }), [])

  return (
    <AdvancedFilter
      ref={advancedFilterRef}
      dataTestID={dataTestID}
      className={className || ''}
      surveySchema={surveySchema}
      pattern={advancedFilterPattern}
      parserResultEof={parserResultEof}
      parserResultPattern={parserResultPattern || parserResultEof}
      prefix={prefix}
      hintPosition={hintPosition}
      disabled={disabled}
      onChange={disabled ? null : onPatternChange}
      onBlur={disabled ? null : onBlur}
    />
  )
})

AdvancedFilterContainer.propTypes = {
  dataTestID: PropTypes.string,
  className: PropTypes.string,
  pattern: PropTypes.string,
  parser: PropTypes.any,
  surveySchema: PropTypes.object,
  clusters: PropTypes.array,
  parserResultEof: PropTypes.object,
  prefix: PropTypes.string,
  hintPosition: PropTypes.oneOf(['top', 'bottom']),
  options: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onUpdate: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onActive: PropTypes.func
}

AdvancedFilterContainer.displayName = 'AdvancedFilterContainer'

export default AdvancedFilterContainer
